import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { StateView } from '../packages/ui';
import { ClipboardList, X, Plus } from 'lucide-react';
import {
    getTemplates, createTemplate, deleteTemplate,
    type TaskTemplate, type CreateTemplateInput,
} from '../packages/api-client/templates-api';

const PRIORITY_OPTS = ['none', 'low', 'medium', 'high', 'urgent'];

interface Props {
    /** When set, "Use Template" button calls this with the template fields. */
    onUse?: (t: TaskTemplate) => void;
}

export function TaskTemplates({ onUse }: Props) {
    const [templates, setTemplates] = useState<TaskTemplate[]>([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [form, setForm] = useState<CreateTemplateInput>({ name: '', description: '', priority: 'none' });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [loadFailed, setLoadFailed] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        setLoadFailed(false);
        getTemplates()
            .then(setTemplates)
            .catch(() => setLoadFailed(true))
            .finally(() => setLoading(false));
    }, []);
    useEffect(load, [load]);

    async function handleCreate(e: React.FormEvent) {
        e.preventDefault();
        if (!form.name.trim()) return;
        setSaving(true); setError('');
        try {
            const t = await createTemplate(form);
            setTemplates(prev => [...prev, t]);
            setForm({ name: '', description: '', priority: 'none' });
            setShowCreate(false);
        } catch {
            setError('Failed to create template');
        } finally {
            setSaving(false);
        }
    }

    async function handleDelete(id: string) {
        if (!confirm('Delete this template?')) return;
        try {
            await deleteTemplate(id);
            setTemplates(prev => prev.filter(t => t.id !== id));
        } catch {
            toast.error('Failed to delete template');
        }
    }

    if (loading) return <StateView state="loading" label="Loading templates" />;
    if (loadFailed) return <StateView state="error" message="Your task templates could not be loaded." onRetry={load} />;

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-3)' }}>
                <span style={{ fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <ClipboardList size={16} aria-hidden="true" />
                    Task Templates ({templates.length})
                </span>
                <button className="btn btn-ghost btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)' }} onClick={() => setShowCreate(s => !s)}>
                    {showCreate ? <><X size={14} aria-hidden="true" /> Cancel</> : <><Plus size={14} aria-hidden="true" /> New Template</>}
                </button>
            </div>

            {showCreate && (
                <form onSubmit={handleCreate} style={{ background: 'var(--color-surface-2)', borderRadius: 'var(--radius-md)', padding: 'var(--space-4)', marginBottom: 'var(--space-3)', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                    {error && <p style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)' }}>{error}</p>}
                    <input className="form-input" required placeholder="Template name *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} autoFocus />
                    <textarea className="form-input" placeholder="Default description" rows={2} style={{ resize: 'vertical' }} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
                    <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                        <select className="form-input" style={{ width: 'auto', flex: 1 }} value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
                            {PRIORITY_OPTS.map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                        <input className="form-input" type="number" min={0} step={0.5} placeholder="Est. hours" style={{ width: 120 }}
                            value={form.estimated_hours ?? ''} onChange={e => {
                                const v = parseFloat(e.target.value);
                                setForm(f => ({ ...f, estimated_hours: isNaN(v) ? undefined : v }));
                            }} />
                    </div>
                    <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
                        <button className="btn btn-secondary btn-sm" type="button" onClick={() => setShowCreate(false)}>Cancel</button>
                        <button className="btn btn-primary btn-sm" type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save Template'}</button>
                    </div>
                </form>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                {templates.length === 0 && (
                    <StateView state="empty" title="No templates yet" message="Create one to reuse task structures quickly." />
                )}
                {templates.map(t => (
                    <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', padding: 'var(--space-3)', background: 'var(--color-surface-2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-border)' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</div>
                            {t.description && <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.description}</div>}
                            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)', marginTop: 2 }}>
                                {t.priority !== 'none' && <span style={{ marginRight: 8 }}>Priority: {t.priority}</span>}
                                {t.estimated_hours && <span>{t.estimated_hours}h est.</span>}
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 'var(--space-1)', flexShrink: 0 }}>
                            {onUse && (
                                <button className="btn btn-primary btn-sm" onClick={() => onUse(t)}>Use</button>
                            )}
                            <button className="btn btn-ghost btn-sm" style={{ color: 'var(--color-danger)' }} onClick={() => handleDelete(t.id)} aria-label="Delete template"><X size={14} aria-hidden="true" /></button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
